import React, { useState } from "react";
import { RxCross1 } from "react-icons/rx";
import axios from "axios";
import SampleInventory from "assets/sample_sheets/Sample_Inventory.xlsx";
import SampleVendor from "assets/sample_sheets/Sample_Vendor.xlsx";
import SampleWorker from "assets/sample_sheets/Sample_Workers.xlsx";
import SampleTransaction from "assets/sample_sheets/Sample_Transactions.xlsx";
import SampleTask from "assets/sample_sheets/Sample_Tasks.xlsx";
import SampleSubTask from "assets/sample_sheets/Sample_Subtasks.xlsx";
import { BASE_URL } from "utils/constant";
import { useAlert } from "../../../components/UI_components/Alert"; // Update with the correct path

const FileUploadModal = ({ onClose }) => {
  const [activeModule, setActiveModule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const { addAlert } = useAlert();

  const modules = [
    {
      name: "Create inventory (new material)",
      sampleFile: SampleInventory,
      endpoint: `${BASE_URL}/inventory/bulk-create-materials/`,
    },
    {
      name: "Create new vendor",
      sampleFile: SampleVendor,
      endpoint: `${BASE_URL}/inventory/vendors/bulk-upload/`,
    },
    {
      name: "Create worker data",
      sampleFile: SampleWorker,
      endpoint: `${BASE_URL}/worker/bulk-upload/`,
    },
    {
      name: "Create new transaction",
      sampleFile: SampleTransaction,
      endpoint: `${BASE_URL}/finance/general-transactions-bulk/upload_transactions/`,
    },
    // {
    //   name: "Create new task",
    //   sampleFile: SampleTask,
    //   endpoint: `${BASE_URL}/task/bulk-upload/`,
    // },
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedFile(file);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    // Reset the file input by clearing its value
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleFileUpload = async (moduleName) => {
    if (!selectedFile) {
      setUploadMessage("Please select a file to upload");
      addAlert("Please select a file to upload", "warning");
      return;
    }

    const module = modules.find((m) => m.name === moduleName);
    if (!module || !module.endpoint) {
      console.error("Module or endpoint not found");
      addAlert("Module or endpoint not found", "error");
      return;
    }

    setIsLoading(true);
    setUploadMessage("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("site_id", localStorage.getItem("site"));

      const response = await axios.post(module.endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      console.log(`Upload successful for ${moduleName}:`, response.data);
      setUploadMessage(`File successfully uploaded for ${moduleName}`);
      addAlert(`File successfully uploaded for ${moduleName}`, "success");

      // Reset the file selection
      setSelectedFile(null);

      setTimeout(() => {
        setActiveModule(null);
      }, 2000);
    } catch (error) {
      console.error(`Error uploading file for ${moduleName}:`, error);
      const errorMessage = error.response?.data?.message || error.message;
      setUploadMessage(`Error uploading file: ${errorMessage}`);
      addAlert(`Error uploading file: ${errorMessage}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSheetLink = async (moduleName) => {
    const linkInput = document.querySelector(`#link-${moduleName}`);
    const link = linkInput?.value;

    if (!link) {
      setUploadMessage("Please enter a valid Google Sheet link");
      addAlert("Please enter a valid Google Sheet link", "warning");
      return;
    }

    const module = modules.find((m) => m.name === moduleName);
    if (!module || !module.endpoint) {
      console.error("Module or endpoint not found");
      addAlert("Module or endpoint not found", "error");
      return;
    }

    setIsLoading(true);
    setUploadMessage("");

    try {
      const response = await axios.post(
        module.endpoint,
        {
          site_id: localStorage.getItem("site"),
          sheet_url: link,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(
        `Google Sheet processing successful for ${moduleName}:`,
        response.data
      );
      setUploadMessage(`Google Sheet successfully processed for ${moduleName}`);
      addAlert(
        `Google Sheet successfully processed for ${moduleName}`,
        "success"
      );

      // Clear the input field
      if (linkInput) {
        linkInput.value = "";
      }

      setTimeout(() => {
        setActiveModule(null);
      }, 2000);
    } catch (error) {
      console.error(`Error processing Google Sheet for ${moduleName}:`, error);
      const errorMessage = error.response?.data?.message || error.message;
      setUploadMessage(`Error processing Google Sheet: ${errorMessage}`);
      addAlert(`Error processing Google Sheet: ${errorMessage}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const getModuleSampleFile = (moduleName) => {
    const module = modules.find((m) => m.name === moduleName);
    return module ? module.sampleFile : null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg w-[60vw] h-[80vh]"
        style={{
          overflowY: "auto",
        }}
      >
        <div className="px-4 py-3 flex gap-12 items-center justify-between mb-6 border-b-[1px] border-[#C3C3C3]">
          <div></div>
          <h2 className="text-xl font-semibold text-center">
            Upload your data
          </h2>
          <RxCross1
            className="text-[#232323] w-5 h-5 cursor-pointer"
            onClick={onClose}
          />
        </div>
        <div className="px-6 pb-6">
          <p className="text-gray-600 mb-6">
            Auto-sync your data here by uploading your spreadsheet
          </p>

          <div className="space-y-4">
            {modules.map((module) => (
              <div
                key={module.name}
                className="bg-[#F3F4F6] rounded-lg px-4 py-2 hover:bg-gray-100 border-[1px] border-[#C3C3C3] transition-colors"
              >
                <div className="flex justify-between items-center">
                  <span className="text-[#2B2B2B] font-medium">
                    {module.name}
                  </span>
                  <button
                    onClick={() => setActiveModule(module.name)}
                    className="flex items-center px-4 py-2 font-semibold text-[#58267F] bg-[#ECE6F0] border-[1px] border-[#C3C3C3] rounded-lg hover:bg-purple-100"
                  >
                    <span className="mr-2">Add File</span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="#58267F"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {activeModule && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-full max-w-xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold">Insert file</h3>
                  <button
                    onClick={() => {
                      setActiveModule(null);
                      setSelectedFile(null);
                      setUploadMessage("");
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                {!selectedFile ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6">
                    <div className="text-center">
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                        disabled={isLoading}
                      />
                      <label
                        htmlFor="file-upload"
                        className={`flex flex-col items-center ${
                          isLoading
                            ? "cursor-not-allowed opacity-70"
                            : "cursor-pointer"
                        }`}
                      >
                        <svg
                          className="w-8 h-8 text-gray-400 mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                        <span className="text-gray-600">
                          Click to browse files
                        </span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-gray-300 rounded-lg p-6 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg
                          className="w-6 h-6 text-gray-500 mr-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <div>
                          <p
                            className="text-sm font-medium text-gray-800 truncate"
                            title={selectedFile.name}
                          >
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={removeSelectedFile}
                        className="ml-2 text-gray-500 hover:text-red-500"
                        title="Remove file"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-gray-600">
                      Or attach google sheets link
                    </p>
                    <a
                      href={getModuleSampleFile(activeModule)}
                      download
                      className="text-[#58267F] font-medium flex items-center hover:underline"
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Download Sample
                    </a>
                  </div>
                  <input
                    type="text"
                    id={`link-${activeModule}`}
                    placeholder="Paste link here"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={isLoading}
                  />
                </div>

                {uploadMessage && (
                  <div
                    className={`p-3 mb-4 rounded-lg ${
                      uploadMessage.includes("Error")
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {uploadMessage}
                  </div>
                )}

                <div className="flex gap-4">
                  {selectedFile && (
                    <button
                      onClick={() => handleFileUpload(activeModule)}
                      className={`flex-1 bg-[#3B0069] text-white py-3 rounded-lg hover:bg-purple-800 transition-colors flex items-center justify-center ${
                        isLoading ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <span>Upload File</span>
                          <svg
                            className="w-4 h-4 ml-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                            />
                          </svg>
                        </>
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => handleGoogleSheetLink(activeModule)}
                    className={`flex-1 bg-[#3B0069] text-white py-3 rounded-lg hover:bg-purple-800 transition-colors flex items-center justify-center ${
                      isLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Upload Sheet</span>
                        <svg
                          className="w-4 h-4 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full mt-6 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
